import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { rupiah, fmtDateTime } from "./format";

const STORE = "TOKO BANGUNAN SUMBER HARAPAN";
const CITY = "Bitung, Sulawesi Utara";

function header(doc, title, subtitle) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text(STORE, 40, 40);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(CITY, 40, 55);
  doc.setDrawColor(249, 115, 22);
  doc.setLineWidth(2);
  doc.line(40, 62, 555, 62);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(title, 40, 82);
  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(subtitle, 40, 96);
  }
}

export function exportSalesPDF({ records, total, pct }, range) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  header(doc, "LAPORAN PENJUALAN", `Periode: ${range}`);
  autoTable(doc, {
    startY: 110,
    head: [["No", "No. Nota", "Tanggal", "Kasir", "Metode", "Total"]],
    body: records.map((s, i) => [
      i + 1, s.number, fmtDateTime(s.created_at), s.cashier || "-",
      (s.payment_method || "").toUpperCase(), rupiah(s.total),
    ]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [15, 23, 42] },
  });
  let y = doc.lastAutoTable.finalY + 20;
  doc.setFont("helvetica", "bold");
  doc.text(`TOTAL PENJUALAN: ${rupiah(total)}`, 40, y);

  if ((pct ?? 100) >= 100) {
    const detail = [];
    records.forEach((s) => {
      (s.items || []).forEach((it, idx) => {
        detail.push([idx === 0 ? s.number : "", it.name, String(it.qty), rupiah(it.price), rupiah(it.discount || 0), rupiah(it.subtotal)]);
      });
    });
    if (detail.length) {
      y += 18;
      doc.setFont("helvetica", "bold"); doc.setFontSize(11);
      doc.text("DETAIL BARANG TERJUAL", 40, y);
      autoTable(doc, {
        startY: y + 8,
        head: [["No. Nota", "Barang", "Qty", "Harga", "Diskon", "Subtotal"]],
        body: detail,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [249, 115, 22] },
      });
    }
  }
  doc.save(`laporan-penjualan-${Date.now()}.pdf`);
}

export function exportPurchasesPDF({ records, total, pct }, range) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  header(doc, "LAPORAN PEMBELIAN", `Periode: ${range}`);
  autoTable(doc, {
    startY: 110,
    head: [["No", "No. Pembelian", "Nota Fisik", "Supplier", "Status", "Total"]],
    body: records.map((p, i) => [
      i + 1, p.number, p.nota || "-", p.supplier,
      p.payment_status === "lunas" ? "Lunas" : "Belum Lunas", rupiah(p.total),
    ]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [15, 23, 42] },
  });
  let y = doc.lastAutoTable.finalY + 20;
  doc.setFont("helvetica", "bold");
  doc.text(`TOTAL PEMBELIAN: ${rupiah(total)}`, 40, y);

  if ((pct ?? 100) >= 100) {
    const detail = [];
    records.forEach((p) => {
      (p.items || []).forEach((it, idx) => {
        detail.push([idx === 0 ? p.number : "", it.name, String(it.qty), rupiah(it.price_buy), `${it.total_disc_pct || 0}%`, rupiah(it.subtotal)]);
      });
    });
    if (detail.length) {
      y += 18;
      doc.setFont("helvetica", "bold"); doc.setFontSize(11);
      doc.text("DETAIL BARANG DIBELI", 40, y);
      autoTable(doc, {
        startY: y + 8,
        head: [["No. Pembelian", "Barang", "Qty", "Harga Beli (Netto)", "Diskon", "Subtotal"]],
        body: detail,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [249, 115, 22] },
      });
    }
  }
  doc.save(`laporan-pembelian-${Date.now()}.pdf`);
}

export function exportFinancePDF({ records, income, expense, opening = 0, pct }, range) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  header(doc, "LAPORAN BUKU KAS", `Periode: ${range}`);
  autoTable(doc, {
    startY: 110,
    head: [["No", "Tanggal", "Jenis", "Kategori", "Keterangan", "Nominal"]],
    body: records.map((c, i) => [
      i + 1, fmtDateTime(c.created_at), c.type === "in" ? "Masuk" : "Keluar",
      c.category || "-", c.description || "-", rupiah(c.amount),
    ]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [15, 23, 42] },
  });
  let y = doc.lastAutoTable.finalY + 24;
  doc.setFontSize(10);
  const line = (l, v, bold) => { doc.setFont("helvetica", bold ? "bold" : "normal"); doc.text(l, 40, y); doc.text(v, 555, y, { align: "right" }); y += 18; };
  line("Saldo Awal", rupiah(opening));
  line("Total Pemasukan", rupiah(income));
  line("Total Pengeluaran", rupiah(expense));
  doc.setDrawColor(200); doc.line(40, y - 8, 555, y - 8);
  line("SALDO KAS", rupiah(opening + income - expense), true);
  doc.save(`laporan-buku-kas-${Date.now()}.pdf`);
}

export function exportDailySeriesPDF(d) {
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });
  header(doc, "LAPORAN KHUSUS MASTER — SALDO AWAL, PENGELUARAN & PENDAPATAN HARIAN", `Periode: ${d.start} s/d ${d.end}`);
  autoTable(doc, {
    startY: 110,
    head: [["Tanggal", "Saldo Awal", "Penjualan", "Pemasukan Lain", "Total Pendapatan", "Pengeluaran", "Est. Saldo Akhir", "Trx"]],
    body: d.rows.map((r) => [r.date, rupiah(r.opening_balance), rupiah(r.revenue), rupiah(r.extra_income), rupiah(r.income), rupiah(r.expense), rupiah(r.closing_estimate), String(r.transactions)]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [15, 23, 42] },
  });
  const y = doc.lastAutoTable.finalY + 20;
  doc.setFont("helvetica", "bold");
  doc.text(`TOTAL PENDAPATAN: ${rupiah(d.total_income)}   TOTAL PENGELUARAN: ${rupiah(d.total_expense)}`, 40, y);
  doc.save(`laporan-khusus-${d.start}_${d.end}.pdf`);
}

export function exportDebtReceiptPDF(payment, debt) {
  const doc = new jsPDF({ unit: "pt", format: [300, 440] });
  doc.setFontSize(13); doc.setFont(undefined, "bold");
  doc.text("TOKO BANGUNAN SUMBER HARAPAN", 150, 30, { align: "center" });
  doc.setFontSize(9); doc.setFont(undefined, "normal");
  doc.text("Bitung, Sulawesi Utara", 150, 44, { align: "center" });
  doc.setFontSize(11); doc.setFont(undefined, "bold");
  doc.text("KUITANSI PEMBAYARAN HUTANG", 150, 66, { align: "center" });
  doc.setDrawColor(150); doc.line(20, 76, 280, 76);
  doc.setFontSize(9); doc.setFont(undefined, "normal");
  let y = 96;
  const row = (l, r) => { doc.text(l, 20, y); doc.text(r, 280, y, { align: "right" }); y += 18; };
  row("Tanggal", new Date(payment.created_at).toLocaleString("id-ID"));
  row("Pelanggan", debt.name || "-");
  row("Metode", payment.method === "transfer" ? "Transfer Bank" : "Tunai");
  if (payment.processed_by) row("Diproses oleh", payment.processed_by);
  doc.line(20, y, 280, y); y += 20;
  doc.setFont(undefined, "bold");
  row("Diterima", rupiah(payment.amount));
  doc.setFont(undefined, "normal");
  row("Sisa Hutang", rupiah(payment.remaining_after));
  doc.setFontSize(8);
  doc.text("Terima kasih atas pembayaran Anda", 150, y + 12, { align: "center" });
  doc.save(`kuitansi-${(debt.name || "pelanggan").replace(/\s+/g, "-")}-${(payment.id || "").slice(0, 6)}.pdf`);
}

export function exportProfitPDF(d) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  header(doc, "LAPORAN ESTIMASI LABA", `Periode: ${d.start} s/d ${d.end}`);
  const s = d.summary;
  autoTable(doc, {
    startY: 110,
    head: [["Ringkasan", "Nilai"]],
    body: [
      ["Omzet (setelah diskon item)", rupiah(s.revenue)],
      ["Diskon Nota", rupiah(s.total_discount)],
      ["Penjualan Bersih", rupiah(s.net_sales)],
      ["HPP / Modal Barang", rupiah(s.cogs)],
      ["LABA KOTOR", rupiah(s.gross_profit)],
      ["Margin Laba", `${s.margin_pct}%`],
      ["Unit Terjual", String(s.units_sold)],
      ["Jumlah Transaksi", String(s.transactions)],
      [`Pajak ${+(((s.tax_rate ?? 0.005)) * 100).toFixed(3)}% (dari total penjualan)`, rupiah(s.tax)],
      ["Pengeluaran Buku Kas", rupiah(s.cash_expense)],
      ["LABA BERSIH", rupiah(s.net_profit)],
    ],
    styles: { fontSize: 10 },
    headStyles: { fillColor: [249, 115, 22] },
  });
  if (!d.limited) {
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 18,
      head: [["Barang", "Qty", "Omzet", "HPP", "Laba", "Margin"]],
      body: d.by_item.map((r) => [r.name, String(r.qty), rupiah(r.revenue), rupiah(r.cost), rupiah(r.profit), `${r.margin_pct}%`]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [15, 23, 42] },
    });
  }
  if (!d.limited) {
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 18,
      head: [["Kategori", "Qty", "Omzet", "HPP", "Laba", "Margin"]],
      body: d.by_category.map((r) => [r.category, String(r.qty), rupiah(r.revenue), rupiah(r.cost), rupiah(r.profit), `${r.margin_pct}%`]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [15, 23, 42] },
    });
  }
  doc.save(`laporan-laba-${d.start}_${d.end}.pdf`);
}

export function exportRecapPDF(r) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  header(doc, "REKAPITULASI HARIAN", `Tanggal: ${r.date}`);
  autoTable(doc, {
    startY: 110,
    head: [["Keterangan", "Nilai"]],
    body: [
      ["Saldo Awal", rupiah(r.opening_balance)],
      ["Total Pendapatan", rupiah(r.revenue)],
      ["Estimasi Laba", rupiah(r.profit)],
      ["Pemasukan Lain", rupiah(r.extra_income)],
      ["Pengeluaran", rupiah(r.expense)],
      ["Jumlah Transaksi", String(r.transactions)],
      ["Estimasi Saldo Akhir", rupiah(r.closing_estimate)],
    ],
    styles: { fontSize: 10 },
    headStyles: { fillColor: [249, 115, 22] },
  });
  doc.save(`rekap-harian-${r.date}.pdf`);
}
